<template>
  <div :id="id" class="chapter-section panel">
    <p class="panel-heading">{{ $t(`chapter10.title-long`) }}</p>

    <!-- Benötigte Berechtigungen -->
    <fieldset class="box">
      <legend>{{ $t('chapter10.q0.title') }}</legend>
      <p>{{ $t('chapter10.q0.text') }}</p>
      <div v-for="option in benoetigteBerechtigungenOptions" :key="option">
        <b-checkbox
          :native-value="option"
          v-model="study.TOMs.benoetigteBerechtigungen"
          @change="onBenoetigteBerechtigungenChange"
        >
          {{ $t(`chapter10.q0.${option}`) }}
        </b-checkbox>
        <!-- Genutzte Primärsysteme -->
        <fieldset
          v-if="
            study.TOMs.benoetigteBerechtigungen.includes('primaersysteme') &&
            option === 'primaersysteme'
          "
        >
          <p>{{ $t('chapter10.q0.primaersystemeText') }}</p>
          <b-field>
            <b-input expanded v-model="study.TOMs.genutztePrimaersysteme" @blur="save" />
          </b-field>
        </fieldset>
      </div>

      <p>{{ $t('chapter10.q0.text2') }}</p>
    </fieldset>

    <!-- Maßnahmen bei der Auswertung -->
    <fieldset class="box">
      <b-field :label="$t(`chapter10.q1.title`)">
        <div v-for="option in massnahmenBeiDerAuswertungOptions" :key="option">
          <b-checkbox
            :required="true"
            :native-value="option"
            v-model="study.TOMs.massnahmenBeiDerAuswertung"
            @change="save"
          >
            {{ $t(`chapter10.q1.${option}`) }}
          </b-checkbox>
        </div>
      </b-field>

      <div v-if="study.TOMs.massnahmenBeiDerAuswertung.includes('weitere')">
        <b-field :label="$t('chapter10.q1.weitereText')">
          <b-input expanded v-model="study.TOMs.massnahmenBeiDerAuswertungWeitere" @blur="save" />
        </b-field>
      </div>
    </fieldset>

    <!-- Räumliche Maßnahmen während der Verarbeitung -->
    <fieldset class="box">
      <b-field :label="$t('chapter10.q2.title')">
        <div v-for="option in raeumlicheOptions" :key="option">
          <b-checkbox
            :required="true"
            :native-value="option"
            v-model="study.TOMs.raeumlicheMassnahmen"
            @change="save"
          >
            {{ $t(`chapter10.q2.${option}`) }}
          </b-checkbox>

          <b-field v-if="study.TOMs.raeumlicheMassnahmen.includes('umf') && option === 'umf'">
            <b-input
              expanded
              :placeholder="$t('chapter10.q2.verarbeitendeKlinik')"
              v-model="study.TOMs.raeumlicheMassnahmenUmfKlinikInstitut"
              @blur="save"
            />
          </b-field>

          <b-field v-if="study.TOMs.raeumlicheMassnahmen.includes('extern') && option === 'extern'">
            <b-input
              expanded
              :placeholder="$t('chapter10.q2.externArtDerVerarbeitung')"
              v-model="study.TOMs.raeumlicheMassnahmenExternArtDerVerarbeitung"
              @blur="save"
            />
          </b-field>
        </div>
      </b-field>

      <fieldset v-if="study.TOMs.raeumlicheMassnahmen.includes('extern')" class="box">
        <legend>{{ $t(`chapter10.q2b.title`) }}</legend>
        <div v-for="option in raeumlicheMassnahmenExternOptions" :key="option">
          <b-checkbox
            :native-value="option"
            v-model="study.TOMs.raeumlicheMassnahmenExtern"
            @change="save"
          >
            {{ $t(`chapter10.q2b.${option}`) }}
          </b-checkbox>
        </div>

        <fieldset
          v-if="
            study.TOMs.raeumlicheMassnahmen.includes('extern') &&
            study.TOMs.raeumlicheMassnahmenExtern.includes('weitere')
          "
        >
          <legend>{{ $t('chapter10.q2b.weitereText') }}</legend>
          <b-field>
            <b-input expanded v-model="study.TOMs.raeumlicheMassnahmenExternWeitere" @blur="save" />
          </b-field>
        </fieldset>
      </fieldset>
    </fieldset>

    <!-- Technische Maßnahmen -->
    <fieldset class="box">
      <legend>
        {{ $t('chapter10.q3.title') }}
        <b-tooltip :label="$t(`general.showRechtstext`)" position="is-right" type="is-warning">
          <b-icon
            type="is-warning"
            icon="format-section"
            class="legal-toggle"
            :class="{ 'is-active': showLegal_c10q3 }"
            @click.native="showLegal_c10q3 = !showLegal_c10q3"
          />
        </b-tooltip>
      </legend>

      <RechtsText v-if="showLegal_c10q3">
        {{ $t('rechtstexte.c10q3a') }}
      </RechtsText>

      <RechtsText v-if="showLegal_c10q3">
        {{ $t('rechtstexte.c10q3b') }}
      </RechtsText>

      <RechtsText v-if="showLegal_c10q3">
        {{ $t('rechtstexte.c10q3c') }}
      </RechtsText>

      <b-field class="is-flex is-justify-content-flex-start">
        <b-switch
          v-model="study.TOMs.individuelleTechnischeOderOrganisatorischeMassnahmen"
          type="is-success"
          @change="onIndividuelleTechnischeOderOrganisatorischeMassnahmenChange"
        >
          {{ $t(`chapter10.q3.textCheckbox`) }}
        </b-switch>
      </b-field>

      <fieldset class="box" v-if="study.TOMs.individuelleTechnischeOderOrganisatorischeMassnahmen">
        <legend>{{ $t('chapter10.q3.technischeMassnahmen') }}</legend>
        <div v-for="option in technischeOptions" :key="option">
          <b-checkbox
            :native-value="option"
            v-model="study.TOMs.technischeMassnahmen"
            @change="save"
          >
            {{ $t(`chapter10.q3.${option}`) }}
          </b-checkbox>
        </div>
        <b-field :label="$t('chapter10.q3.weitereTechnische')">
          <b-input expanded v-model="study.TOMs.weitereTechnischeMassnahmen" @blur="save" />
        </b-field>
      </fieldset>

      <!-- Organisatorische Maßnahmen -->
      <fieldset class="box" v-if="study.TOMs.individuelleTechnischeOderOrganisatorischeMassnahmen">
        <legend>{{ $t('chapter10.q3.organisatorischeMassnahmen') }}</legend>
        <div v-for="option in organisatorischeOptions" :key="option">
          <b-checkbox
            :native-value="option"
            v-model="study.TOMs.organisatorischeMassnahmen"
            @change="save"
          >
            {{ $t(`chapter10.q3.${option}`) }}
          </b-checkbox>
        </div>
        <b-field :label="$t('chapter10.q3.weitereOrganisatorische')">
          <b-input expanded v-model="study.TOMs.weitereOrganisatorischeMassnahmen" @blur="save" />
        </b-field>
      </fieldset>
    </fieldset>
  </div>
</template>

<script setup lang="ts">
import { useStudyStore } from '@/stores/studyStore'
import { useRechtstextToggle } from '@/composables/useVisibilityToggles'
import RechtsText from '@/components/RechtsText.vue'
import { BCheckbox, BField, BIcon, BInput, BSwitch, BTooltip } from 'buefy'

defineProps<{ id: string; modelValue?: object }>()

const store = useStudyStore()
const study = store.study
const title = 'TOMs (Technische und Organisatorische Maßnahmen)'

const benoetigteBerechtigungenOptions = ['klinet', 'klifo', 'primaersysteme']
const raeumlicheOptions = ['umf', 'extern']
const massnahmenBeiDerAuswertungOptions = [
  'passwortRegelungGUF',
  'rollenUndBerechtigungskonzepte',
  'profile',
  'mfa',
  'weitere',
]
const raeumlicheMassnahmenExternOptions = [
  'gebaeudesicherung',
  'zutrittsregelungen',
  'sicherheitspersonal',
  'sicherungServerraeume',
  'weitere',
]
const technischeOptions = [
  'datenVerschluesselung',
  'transportVerschluesselung',
  'physischeZugangskontrolle',
  'MFA',
  'protokollierung',
  'trennungDatenUndBackups',
  'routineBackups',
  'isoAnforderungenErfuellt',
]
const organisatorischeOptions = [
  'schulungVonMitarbeitern',
  'nda',
  'audits',
  'sicherheitsvorfallErkennungUndBehandlung',
  'meldungUnterauftragnehmer',
  'datenschutzkonformeLoeschung',
]

const save = () => {
  store.updateChapter('TOMs', study.TOMs)
}

const showLegal_c10q3 = useRechtstextToggle()

function onBenoetigteBerechtigungenChange() {
  if (!(study.TOMs.benoetigteBerechtigungen || []).includes('primaersysteme')) {
    study.TOMs.genutztePrimaersysteme = undefined
  }
  save()
}

function onIndividuelleTechnischeOderOrganisatorischeMassnahmenChange(event: Event) {
  const newValue = (event.target as HTMLInputElement).checked
  if (!newValue) {
    study.TOMs.technischeMassnahmen = []
    study.TOMs.weitereTechnischeMassnahmen = undefined
    study.TOMs.organisatorischeMassnahmen = []
    study.TOMs.weitereOrganisatorischeMassnahmen = undefined
  }
  save()
}
</script>

<style scoped></style>
